/**
 * Normaliza uma string de data removendo timestamp se existir
 * @param dateString - String da data no formato ISO ou YYYY-MM-DD
 * @returns String da data no formato YYYY-MM-DD
 */
export const normalizeDate = (dateString: string): string => {
  if (!dateString) return '';
  // Se contém timestamp, extrair apenas a data
  if (dateString.includes('T')) {
    return dateString.split('T')[0];
  }
  return dateString;
};

/**
 * Formata uma data para exibição em português brasileiro
 * @param dateString - String da data
 * @param includeTime - Se deve incluir hora na formatação
 * @returns String formatada da data
 */
export const formatDate = (dateString: string, includeTime: boolean = false): string => {
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return 'Data inválida';
    }
    
    if (includeTime) {
      return date.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
    
    // Para datas sem hora, usar UTC para evitar problemas de fuso horário
    if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
      // Se é formato YYYY-MM-DD, construir data local diretamente
      const [year, month, day] = dateString.split('-');
      const localDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      return localDate.toLocaleDateString('pt-BR');
    }
    
    return date.toLocaleDateString('pt-BR');
  } catch (error) {
    return 'Data inválida';
  }
};

/**
 * Formata uma data de vencimento usando normalizeDate + formatDate
 * @param dateString - String da data de vencimento
 * @returns String formatada da data de vencimento
 */
export const formatDueDate = (dateString: string): string => {
  return formatDate(normalizeDate(dateString));
};

export type CurrencyCode = 'BRL' | 'USD';

/**
 * Formata um valor monetário para exibição
 * @param value - Valor numérico
 * @param currency - Código da moeda (BRL ou USD)
 * @returns String formatada do valor monetário
 */
export const formatCurrency = (value: number, currency: CurrencyCode = 'BRL'): string => {
  if (currency === 'USD') {
    // US$ com formatação americana (1,234.56)
    const formatted = value.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    return `US$ ${formatted}`;
  }
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value);
};

/**
 * Formata um valor monetário de forma resumida (ex: 4M, 500K)
 * @param value - Valor numérico
 * @returns String formatada resumida
 */
export const formatCurrencyShort = (value: number): string => {
  if (value >= 1000000) {
    const millions = value / 1000000;
    // Se for número inteiro, não mostrar decimais
    if (millions % 1 === 0) {
      return `${millions.toFixed(0)}M`;
    }
    return `${millions.toFixed(1).replace('.', ',')}M`;
  } else if (value >= 1000) {
    const thousands = value / 1000;
    // Se for número inteiro, não mostrar decimais
    if (thousands % 1 === 0) {
      return `${thousands.toFixed(0)}K`;
    }
    return `${thousands.toFixed(1).replace('.', ',')}K`;
  }
  return value.toFixed(0);
};
